/*
 *   File : listing.js
 *   Author URI : https://evoqins.com
 *   Description : KYC page
 *   Integrations : null
 *   Version : v1.1
 */

/* import packages */
import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { Fragment } from "react";
import { toast } from "react-toastify";
import { Modal, Offcanvas } from "bootstrap";
import { format } from "date-fns";
import moment from "moment";

/* import components */
import { NavigationHeader } from "../../Components/Header";
import { UserSummary } from "../../Components/Cards";
import { DataTableContainer, SearchInput, CustomSearchSelectBox, CustomDateRangePicker, CustomTimePicker } from "../../Components/FormElements";
import { DownloadButton } from "../../Components/Cta";
import { ApproveKYCModal, RejectKYCModal, UnlockSIPModal, FileViewer } from "../../Components/Modal";
import { Icon } from "../../Components/Icon";
import { CustomLoader, EmptyScreen } from "../../Components/Other";

/* import styles */
import Colors from '../../Styles/color.module.scss';

/* import services */
import APIService from "../../Services/api-service";
import { _manageKyc } from "../../Helper/api";


const TABLE_STYLE = {
    table: {
        style: {
            padding: "0px",
            borderRadius: "16px",
            background: Colors.white,
            border: `1px solid ${Colors.bright_gray}`,
        },
    },
    tableBody: {
        style: {
            margin: "0px",
            marginTop: "0px !important"
        }
    },
    headRow: {
        style: {
            background: Colors.gray_50,
            borderRadius: "16px 16px 0px 0px",
            border: "0",
            padding: "16px 24px",
            minHeight: "56px",
            fontSize: "12px",
            fontFamily: "Poppins-Bold",
            lineHeight: "16px",
            color: Colors.gray_700,
            textTransform: "uppercase",
        }
    },
    rows: {
        style: {
            margin: "0",
            background: "transparent",
            border: "0 !important",
            borderTop: `1px solid ${Colors.bright_gray} !important`,
            borderRadius: "0",
            fontSize: "14px",
            fontFamily: "Poppins-Regular",
            lineHeight: "24px",
            cursor: "pointer",
            padding: "16px 24px",
            '&:hover': {
                backgroundColor: Colors.gray_50,
            },
        },
    },
    cells: {
        style: {
            paddingLeft: "0px",
            paddingRight: "12px",
        },
    },
    headCells: {
        style: {
            paddingLeft: "0px",
            paddingRight: "12px",
            textTransform: "uppercase",
            fontSize: "12px",
            fontFamily: "Poppins-Bold",
            lineHeight: "16px",
            color: Colors.gray_700,
            letterSpacing: "0.02em",
        },
    },
    pagination: {
        style: {
            display: "none",
        },
    }
}

const ONBOARDING_STATUS_CHIP_MAP = {
    approved: { bg: "#ECFDF5", text: "#059669", dot: "#059669" },
    "in process": { bg: "#EFF6FF", text: "#2563EB", dot: "#2563EB" },
    "in progress": { bg: "#EFF6FF", text: "#2563EB", dot: "#2563EB" },
    "review pending": { bg: "#FFFBEB", text: "#D97706", dot: "#D97706" },
    rejected: { bg: "#FEF2F2", text: "#DC2626", dot: "#DC2626" },
    dedupe: { bg: "#F5F3FF", text: "#7C3AED", dot: "#7C3AED" },
    deleted: { bg: "#F3F4F6", text: "#4B5563", dot: "#4B5563" },
    cancelled: { bg: "#F3F4F6", text: "#4B5563", dot: "#4B5563" },
};

const OnboardingStatusChip = ({ label }) => {
    const normalized = label?.trim().toLowerCase();
    const chipStyle = ONBOARDING_STATUS_CHIP_MAP[normalized];

    if (!label || !chipStyle) {
        return <span className="color-outer-space">—</span>;
    }

    return (
        <span className="e-status-chip d-inline-flex align-items-center gap-6px e-poppins-regular e-font-12 line-height-16px white-space-nowrap"
            style={{ backgroundColor: chipStyle.bg, color: chipStyle.text }}>
            <span className="e-status-chip-dot" style={{ backgroundColor: chipStyle.dot }} />
            {label}
        </span>
    );
};

const _formatTableCell = (value) => {
    if (value === null || value === undefined || value === "") {
        return "—";
    }
    return value;
};

// FileViewer renders type 2 as <img>, anything else through the Google Docs (PDF) viewer —
// image files (jpg/jpeg/jfif/png/...) must resolve to type 2 or they fail to preview
const IMAGE_FILE_EXTENSIONS = ["jpg", "jpeg", "jfif", "png", "gif", "webp", "bmp", "svg", "heic", "heif"];

const _getFileViewerType = (url) => {
    if (!url) {
        return 1;
    }
    const path = url.split("?")[0];
    const extension = path.split(".").pop()?.toLowerCase();
    return IMAGE_FILE_EXTENSIONS.includes(extension) ? 2 : 1;
};

const SUMMARY_BORDER_COLOR_MAP = {
    all: Colors.indigo_500,
    total: Colors.indigo_500,
    "review pending": Colors.amber_500,
    "in process": Colors.blue_500,
    approved: Colors.green_500,
    rejected: Colors.red_500,
    dedupe: Colors.purple_500,
    deleted: Colors.gray_500,
    cancelled: Colors.gray_500,
};

// All/Total stay black — only status-specific cards color their count to match the accent bar
const SUMMARY_COUNT_COLOR_MAP = {
    "review pending": Colors.amber_500,
    "in process": Colors.blue_500,
    approved: Colors.green_500,
    rejected: Colors.red_500,
    dedupe: Colors.purple_500,
    deleted: Colors.gray_500,
    cancelled: Colors.gray_500,
};

const SUMMARY_TOOLTIP_MAP = {
    all: "Total records",
    total: "Processed records",
};

// static fallbacks — values are the backend ids/null (not labels) so the initial
// payload matches the contract before filter-data-new loads and replaces these
const TYPE_FILTER_OPTIONS = [
    { value: null, label: "All" },
    { value: 1, label: "Individual" },
    { value: 2, label: "Minor" },
];

const CREATED_DATE_FILTER_OPTIONS = [
    { value: 1, label: "Created Date" },
    { value: 2, label: "Esign Date" },
];

const MODE_FILTER_OPTIONS = [
    { value: null, label: "All" },
    { value: 1, label: "KRA" },
    { value: 2, label: "Digilocker" },
];

const KRA_STATUS_FILTER_OPTIONS = [
    { value: null, label: "All" },
    { value: 3, label: "Validated" },
    { value: 4, label: "On Hold" },
];

const STATUS_FILTER_OPTIONS = [
    { value: null, label: "All" },
    { value: 1, label: "Approved" },
    { value: 2, label: "In Process" },
    { value: 3, label: "Rejected" },
    { value: 4, label: "Review Pending" },
];

const SOURCE_FILTER_OPTIONS = [
    { value: null, label: "All" },
    { value: 1, label: "Direct" },
    { value: 2, label: "RM Link" },
];

const _getSummaryBorderColor = (label) => {
    const key = label?.trim().toLowerCase();
    return SUMMARY_BORDER_COLOR_MAP[key] || Colors.primary;
};

const _getSummaryCountColor = (label) => {
    const key = label?.trim().toLowerCase();
    return SUMMARY_COUNT_COLOR_MAP[key] || Colors.black;
};

// show "-" for any missing value while keeping the layout intact
const _dash = (value) => (value === null || value === undefined || value === "" ? "-" : value);

const DrawerHeaderField = ({ label, value }) => (
    <div className="mb-2 text-truncate" title={value ? `${label}: ${value}` : undefined}>
        <span className="color-gray-500 e-poppins-regular e-font-14 line-height-20px">{label}: </span>
        <span className="color-gray-500 e-poppins-regular e-font-14 line-height-20px">{_dash(value)}</span>
    </div>
);

const DrawerRowField = ({ label, value }) => (
    <div className="d-flex align-items-start justify-content-between gap-3 mb-2">
        <span className="color-yankees-blue e-poppins-regular e-font-14 line-height-20px white-space-nowrap">{label}:</span>
        <span className="color-gray-700 e-poppins-medium e-font-14 line-height-20px text-end word-break-all">{_dash(value)}</span>
    </div>
);

const DrawerStackField = ({ label, value }) => (
    <div className="mb-3">
        <p className="color-gray-500 e-poppins-regular e-font-14 line-height-20px mb-1">{label}</p>
        <p className="color-gray-700 e-poppins-medium e-font-14 line-height-20px mb-0 word-break-all">{_dash(value)}</p>
    </div>
);

const DrawerSectionTitle = ({ icon, title }) => (
    <div className="d-flex align-items-center gap-8px mb-3">
        <Icon icon={icon} size={20} />
        <span className="color-yankees-blue e-poppins-semi-bold e-font-16 line-height-24px">{title}</span>
    </div>
);

const DRAWER_PILL_TONES = {
    positive: { bg: "#ECFDF5", text: "#059669" },
    negative: { bg: "#FEF2F2", text: "#DC2626" },
    neutral: { bg: "#F3F4F6", text: "#4B5563" },
    info: { bg: "#EFF6FF", text: "#2563EB" },
};

const DRAWER_PILL_STATUS_TONE = {
    yes: "positive", accepted: "positive", completed: "positive", verified: "positive", approved: "positive", uploaded: "positive",
    success: "positive", validated: "positive", created: "positive",
    no: "neutral", "not accepted": "neutral", unchecked: "neutral",
    rejected: "negative", failed: "negative",
    pending: "info", "in process": "info", "in progress": "info", "review pending": "info", "on hold": "info",
};

// boolean -> badge label; null/undefined keeps the layout but renders "-"
const _boolBadge = (value, trueLabel, falseLabel) => {
    if (value === true) return trueLabel;
    if (value === false) return falseLabel;
    return null;
};

// status-badge glyphs exported from Figma; neutral ("No") has no Figma glyph so keep a simple cross
const PILL_TONE_ICON = {
    positive: "verification-completed",
    negative: "verification-rejected",
    info: "verification-pending",
};

const _renderPillIcon = (tone, color) => {
    if (PILL_TONE_ICON[tone]) {
        return <Icon icon={PILL_TONE_ICON[tone]} size={14} color={color} />;
    }
    return <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M3.2 3.2 8.8 8.8M8.8 3.2 3.2 8.8" stroke={color} strokeWidth="1.6" strokeLinecap="round" /></svg>;
};

const StatusPill = ({ status }) => {
    if (status === null || status === undefined || status === "") {
        return <span className="color-outer-space e-poppins-regular e-font-14">-</span>;
    }
    const tone = DRAWER_PILL_STATUS_TONE[String(status).trim().toLowerCase()] || "info";
    const palette = DRAWER_PILL_TONES[tone];
    return (
        <span className="d-inline-flex align-items-center gap-6px e-poppins-medium e-font-12 line-height-16px white-space-nowrap"
            style={{ backgroundColor: palette.bg, color: palette.text, borderRadius: "999px", padding: "4px 10px" }}>
            {_renderPillIcon(tone, palette.text)}
            {status}
        </span>
    );
};

// Declarations status chip (Figma): green + form-complete for Yes/Accepted,
// grey + status-icon otherwise. Takes the already-mapped label from _boolBadge,
// so the underlying API field mapping stays unchanged.
const _renderDeclarationChip = (label) => {
    if (label === null || label === undefined || label === "") {
        return <span className="color-outer-space e-poppins-regular e-font-14 line-height-20px">-</span>;
    }
    const isPositive = ["yes", "accepted"].includes(String(label).trim().toLowerCase());
    return (
        <span className={`e-declaration-chip e-poppins-medium e-font-12 line-height-20px ${isPositive ? "bg-green-100 color-green-800" : "bg-gray-100 color-gray-600"}`}>
            <Icon icon={isPositive ? "form-complete" : "status-icon"} size={16} />
            {label}
        </span>
    );
};

const ManageOnboarding = React.memo((props) => {
    const navigate = useNavigate();
    const location = useLocation();
    const ADMIN_PROFILE_DATA = useSelector(state => state.MT_ADMIN_STORE.ADMIN_PROFILE);

    const [onHoverApprove, setOnHoverApprove] = useState(null);
    const [onHoverReject, setOnHoverReject] = useState(null);
    const [approveModal, setApproveModal] = useState(false);
    const [rejectModal, setRejectModal] = useState(false);
    const [tabData, setTabData] = useState([]);
    const [tabIndex, setTabIndex] = useState(null);
    const [onboardingList, setOnboardingList] = useState({});
    const [searchQuery, setSearchQuery] = useState("");
    const [loader, setLoader] = useState(true);
    const [apiLoader, setApiLoader] = useState(true);
    const [rejectCustomer, setRejectCustomer] = useState({})
    const [reasonList, setReasonList] = useState([]);
    const [summaryData, setSummaryData] = useState([]);
    const [pageNumber, setPageNumber] = useState(1);
    const [totalPages, setTotalPages] = useState(null);

    const [downloadLoader, setDownloadLoader] = useState(false);
    const [unlockModal, setUnlockModal] = useState(false);
    const [selectAll, setSelectAll] = useState(false);
    const [selectedCustomers, setSelectedCustomers] = useState(new Set());
    const [rowSelectFalse, setRowSelectFalse] = useState(false);

    const [paginationData, setPaginationData] = useState(null);
    const [pageSize, setPageSize] = useState(20);
    const [totalLength, setTotalLength] = useState(null);
    const [permissionType, setPermissionType] = useState('');
    const [file, setFile] = useState('');
    const [openFileViewer, setOpenFileViewer] = useState(false);
    const [actionLoader, setActionLoader] = useState(false);

    const [onboardingFlowTypes, setOnboardingFlowTypes] = useState([]);
    const [onboardingFlowType, setOnboardingFlowType] = useState(null);

    // created date-range & time filters (list-new: start_date/end_date/start_time/end_time)
    const popupRef = useRef(null);
    const [selectedRange, setSelectedRange] = useState({
        startDate: new Date(),
        endDate: new Date(),
        key: 'selection',
    });
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [showDateFilter, setShowDateFilter] = useState(false);
    const [selectedStartTime, setSelectedStartTime] = useState(null);
    const [selectedStartTimeError, setSelectedStartTimeError] = useState('');
    const [selectedEndTime, setSelectedEndTime] = useState(null);
    const [selectedEndTimeError, setSelectedEndTimeError] = useState('');
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);
    const [detailData, setDetailData] = useState(null);
    const [detailLoader, setDetailLoader] = useState(false);
    // verification approve/reject confirmation modal
    const [confirmAction, setConfirmAction] = useState(null); // 'approve' | 'reject' | null
    const [confirmLoader, setConfirmLoader] = useState(false);
    const [repushLoader, setRepushLoader] = useState(false);

    // UI-only filter state — TODO: API wire when backend supports these filters
    const [uiTypeFilter, setUiTypeFilter] = useState(TYPE_FILTER_OPTIONS[1]); // default Individual (kyc_onboarding_flow = 1)
    const [uiCreatedDateFilter, setUiCreatedDateFilter] = useState(CREATED_DATE_FILTER_OPTIONS[0]);
    const [uiModeFilter, setUiModeFilter] = useState(MODE_FILTER_OPTIONS[0]);
    const [uiKraStatusFilter, setUiKraStatusFilter] = useState(KRA_STATUS_FILTER_OPTIONS[0]);
    const [uiStatusFilter, setUiStatusFilter] = useState(STATUS_FILTER_OPTIONS[0]);
    const [uiSourceFilter, setUiSourceFilter] = useState(SOURCE_FILTER_OPTIONS[0]);
    // bumped on reset to force-remount SearchInput (it seeds from defaultValue only on mount)
    const [searchResetKey, setSearchResetKey] = useState(0);

    // dropdown options sourced from filter-data-new (static arrays are fallback until API loads)
    const [typeFilterOptions, setTypeFilterOptions] = useState(TYPE_FILTER_OPTIONS);
    const [modeFilterOptions, setModeFilterOptions] = useState(MODE_FILTER_OPTIONS);
    const [kraStatusFilterOptions, setKraStatusFilterOptions] = useState(KRA_STATUS_FILTER_OPTIONS);
    const [statusFilterOptions, setStatusFilterOptions] = useState(STATUS_FILTER_OPTIONS);
    const [sourceFilterOptions, setSourceFilterOptions] = useState(SOURCE_FILTER_OPTIONS);

    useEffect(() => {
        _getOnboardingFilterData();
    }, []);

    useEffect(() => {
        const page_type = ADMIN_PROFILE_DATA.permission_details.filter((permission) => location.pathname.includes(permission.short_name.toLowerCase().replace(/_/g, '-')))
        if (page_type.length > 0) {
            setPermissionType(page_type[0].action);
        }
    }, [ADMIN_PROFILE_DATA, location]);

    useEffect(() => {
        window.scrollTo({
            top: 0,
            behavior: 'smooth'
        });
        _getOnboardingList();
        //eslint-disable-next-line
    }, [tabIndex, pageNumber, pageSize, onboardingFlowType, uiTypeFilter, uiCreatedDateFilter, uiModeFilter, uiKraStatusFilter, uiStatusFilter, uiSourceFilter, startDate, endDate, selectedStartTime, selectedEndTime])

    // open kyc success modal
    useEffect(() => {
        if (approveModal === true) {
            const modal = new Modal(document.getElementById("kyc-success"));
            modal.show();
        }
    }, [approveModal]);

    // open kyc reject modal
    useEffect(() => {
        if (rejectModal === true) {
            const modal = new Modal(document.getElementById("reject-kyc"));
            modal.show();
        }
    }, [rejectModal]);


    useEffect(() => {
        if (unlockModal === true) {
            const modal = new Modal(document.getElementById("unlock"));
            modal.show();
        }
    }, [unlockModal])

    useEffect(() => {
        const drawerEl = document.getElementById("onboarding-detail-drawer");
        if (!drawerEl) {
            return;
        }

        const offcanvas = Offcanvas.getOrCreateInstance(drawerEl);

        const _onDrawerHidden = () => {
            setDrawerOpen(false);
            setSelectedRow(null);
            setDetailData(null);
        };

        drawerEl.addEventListener("hidden.bs.offcanvas", _onDrawerHidden);

        if (drawerOpen === true) {
            offcanvas.show();
        }

        return () => {
            drawerEl.removeEventListener("hidden.bs.offcanvas", _onDrawerHidden);
        };
    }, [drawerOpen]);

    // API - approve kyc
    const _handleApproveModal = (row) => {
        setActionLoader(true);
        const data = {
            "customer_id": row.customer_id,
            "is_approve": true,
            "reason_id": null,
            "reason": null
        }
        _manageKyc(data)
            .then((response) => {
                setApproveModal(true);
            }).catch((response) => {
                setActionLoader(false);
                _getOnboardingList();
                toast.dismiss();
                toast.error(response, {
                    type: "error"
                });
            });
    }

    // date range handlers
    function _handleDateRangeChange(newRange) {
        setShowDateFilter(false);
        setStartDate(format(newRange.startDate, "dd-MM-yyyy"));
        setEndDate(format(newRange.endDate, "dd-MM-yyyy"));
        setSelectedRange(newRange);
        setPageNumber(1);
    };

    function _handleShowDateFilter() {
        setShowDateFilter(!showDateFilter);
    }

    function _handleClearDates() {
        setShowDateFilter(false);
        setSelectedRange({
            startDate: new Date(),
            endDate: new Date(),
            key: 'selection',
        });
        setStartDate(null);
        setEndDate(null);
        setPageNumber(1);
    }

    function _handleResetFilters() {
        const defaultTabId = tabData[0]?.id;
        const defaultFlowId = onboardingFlowTypes[0]?.id;

        if (defaultTabId !== undefined) {
            _handleTabIndex(defaultTabId);
        } else {
            _handleClearDates();
            setSelectedStartTime(null);
            setSelectedEndTime(null);
            setSelectedStartTimeError("");
            setSelectedEndTimeError("");
        }

        if (defaultFlowId !== undefined) {
            setOnboardingFlowType(defaultFlowId);
        }

        setUiTypeFilter(typeFilterOptions[0]); // default All
        setUiCreatedDateFilter(CREATED_DATE_FILTER_OPTIONS[0]);
        setUiModeFilter(modeFilterOptions[0]);
        setUiKraStatusFilter(kraStatusFilterOptions[0]);
        setUiStatusFilter(statusFilterOptions[0]);
        setUiSourceFilter(sourceFilterOptions[0]);

        _handleSearchQuery("");
        setSearchResetKey((prev) => prev + 1);
    }

    // filter changes update state + reset to page 1; the list effect refetches list-new
    const _handleTypeFilterUiChange = (option) => {
        setUiTypeFilter(option);
        setPageNumber(1);
    };

    // Created Date / Esign date-type dropdown removed — date filtering uses the start/end date range
    // const _handleCreatedDateFilterUiChange = (option) => {
    //     setUiCreatedDateFilter(option);
    //     setPageNumber(1);
    // };

    const _handleModeFilterUiChange = (option) => {
        setUiModeFilter(option);
        setPageNumber(1);
    };

    const _handleKraStatusFilterUiChange = (option) => {
        setUiKraStatusFilter(option);
        setPageNumber(1);
    };

    const _handleStatusFilterUiChange = (option) => {
        setUiStatusFilter(option);
        setPageNumber(1);
    };

    // summary card click drives the same status filter used by the Status dropdown —
    // reuse the matching dropdown option (by filter_id) so the dropdown stays in sync,
    // falling back to a synthetic option for statuses the dropdown doesn't list (e.g. Dedupe, Deleted)
    const _handleSummaryCardClick = (card) => {
        if (apiLoader === true) {
            return;
        }
        const matched_option = statusFilterOptions.find((option) => option.value === card.filter_id);
        setUiStatusFilter(matched_option || { value: card.filter_id, label: card.label });
        setPageNumber(1);
    };

    const _handleSourceFilterUiChange = (option) => {
        setUiSourceFilter(option);
        setPageNumber(1);
    };

    // time picker handlers
    const _handleSelectStartTime = (selected_time) => {
        if (!selected_time) {
            setSelectedStartTime(null);
            setSelectedStartTimeError("Please select a valid time");
        } else {
            const formattedTime = selected_time.split('.')[0];
            setSelectedStartTime(formattedTime);
            setSelectedStartTimeError("");
        }
        setPageNumber(1);
    };

    const _handleSelectEndTime = (selected_end_time) => {
        if (!selected_end_time) {
            setSelectedEndTime(null);
            setSelectedEndTimeError("Please select a valid time");
        } else {
            const formattedTime = selected_end_time.split('.')[0];
            setSelectedEndTime(formattedTime);
            setSelectedEndTimeError("");
        }
        setPageNumber(1);
    };

    const _getDisabledEndTimes = () => {
        if (!selectedStartTime) return {};

        const start = moment(selectedStartTime, "HH:mm:ss.SSS");
        const disabledHours = [];
        const disabledMinutesMap = {};

        for (let i = 0; i < start.hour(); i++) {
            disabledHours.push(i);
        }

        for (let m = 0; m < start.minute(); m++) {
            if (!disabledMinutesMap[start.hour()]) {
                disabledMinutesMap[start.hour()] = [];
            }
            disabledMinutesMap[start.hour()].push(m);
        }

        return {
            disabledHours: () => disabledHours,
            disabledMinutes: (selectedHour) => {
                return disabledMinutesMap[selectedHour] || [];
            },
        };
    };

    const _handleRejectModal = (row) => {
        setRejectModal(true);
        setActionLoader(true);
        setRejectCustomer(row)
    }

    const _handleOpenDrawer = (row, event) => {
        // UCC cell click bubbles to the row handler too — stop it to avoid a duplicate details call
        event?.stopPropagation();
        setSelectedRow(row);
        setDrawerOpen(true);
        // reuse already-loaded details for the same customer; fetch only when the selection changes
        if (detailData?.customer_id !== row.customer_id) {
            setDetailData(null);
            _getOnboardingDetail(row.customer_id);
        }
    }

    // API - get onboarding detail for drawer (reduced view from flat `details` response)
    const _getOnboardingDetail = (customer_id) => {
        setDetailLoader(true);
        const url = 'onboarding/kyc/details';
        const payload = JSON.stringify({ customer_id });
        APIService(true, url, payload).then((response) => {
            if (response.status_code === 200) {
                setDetailData(response.data);
            } else {
                setDetailData(null);
                toast.dismiss();
                toast.error(response.message, { type: "error" });
            }
            setDetailLoader(false);
        });
    }

    const _handleCloseDrawer = () => {
        const drawerEl = document.getElementById("onboarding-detail-drawer");
        if (drawerEl) {
            const offcanvas = Offcanvas.getInstance(drawerEl);
            offcanvas?.hide();
        } else {
            setDrawerOpen(false);
            setSelectedRow(null);
        }
    }

    // confirm-modal driven approve/reject — reuses the existing _manageKyc (onboarding/kyc/action)
    const _handleConfirmKycAction = () => {
        if (!detailData || confirmLoader) {
            return;
        }
        const is_approve = confirmAction === 'approve';
        setConfirmLoader(true);
        _manageKyc({
            customer_id: detailData.customer_id,
            is_approve: is_approve,
            reason_id: null,
            reason: null,
        }).then(() => {
            setConfirmLoader(false);
            setConfirmAction(null);
            _handleCloseDrawer();
            _getOnboardingList();
            toast.dismiss();
            toast.success(is_approve ? "Verification approved" : "Verification rejected", { type: "success" });
        }).catch((error) => {
            setConfirmLoader(false);
            toast.dismiss();
            toast.error(error, { type: "error" });
        });
    }

    // API - repush kyc (rejected records only) — same endpoint used on the KYC detail page
    const _repushKyc = () => {
        if (!detailData || repushLoader) {
            return;
        }
        setRepushLoader(true);
        const url = 'onboarding/kyc/repush';
        const payload = JSON.stringify({ customer_id: detailData.customer_id });
        APIService(true, url, payload).then((response) => {
            toast.dismiss();
            if (response.status_code === 200) {
                toast.success("KYC repushed successfully", { type: "success" });
                _getOnboardingDetail(detailData.customer_id);
                _getOnboardingList();
            } else {
                toast.error(response.message, { type: "error" });
            }
            setRepushLoader(false);
        });
    }

    // navigate to onboarding detail page
    const _handleCardClick = (row) => {
        navigate('/manage-onboarding/detail', {
            state: {
                customer_id: row.customer_id,
            },
        });
    }

    const _handleTabIndex = (id) => {
        setTabIndex(id);
        // reset date/time filters when switching main tabs
        setSelectedRange({
            startDate: new Date(),
            endDate: new Date(),
            key: 'selection',
        });
        setStartDate(null);
        setEndDate(null);
        setSelectedStartTime(null);
        setSelectedEndTime(null);
        setSelectedStartTimeError("");
        setSelectedEndTimeError("");
        setShowDateFilter(false);
        setPageNumber(1);
    }

    const _handleSearchQuery = (value) => {
        setPageNumber(1);
        setSearchQuery(value)
        if (value === "") {
            _getOnboardingList();
        }
    }

    // handler for page change
    function _nextPage() {
        setPageNumber(prevCount => prevCount + 1);
    }

    // got to previous page
    function _previousPage() {
        setPageNumber(prevCount => prevCount - 1);
    }

    // API - get kyc filter data
    const _getOnboardingFilterData = () => {
        let url = 'onboarding/kyc/filter-data-new';
        APIService(false, url).then((response) => {
            if (response.status_code === 200) {
                const modified_data = response.data.filter_data.map((item) => {
                    return {
                        id: item.id,
                        type: item.label
                    }
                })
                const onboarding_sub_type = response.data.kyc_onboarding_flow
                    // NRI tab hidden for go-live (may-delivery). Re-enable post-release.
                    .filter((item) => item.label?.trim().toLowerCase() !== "nri")
                    .map((item) => {
                        return {
                            id: item.id,
                            type: item.label
                        }
                    })
                setTabData(modified_data);
                setOnboardingFlowTypes(onboarding_sub_type)
                setTabIndex(response.data.filter_data[0].id)
                setOnboardingFlowType(onboarding_sub_type[0].id);

                // filter-data-new: source dropdown options from API (value = id, label = label)
                const _mapFilterOptions = (list) => (list || []).map((item) => ({ value: item.id, label: item.label }));
                // Type dropdown is sourced from the full kyc_onboarding_flow list
                // (All, Individual=1, Minor=2, NRI=10). NRI is hidden only for the
                // onboarding tabs (onboarding_sub_type), not for this filter.
                const type_options = [
                    { value: null, label: "All" },
                    ...(response.data.kyc_onboarding_flow || []).map((item) => ({ value: item.id, label: item.label })),
                ];
                const mode_options = _mapFilterOptions(response.data.mode);
                const kra_status_options = _mapFilterOptions(response.data.kra_status);
                const status_options = _mapFilterOptions(response.data.status);
                const source_options = _mapFilterOptions(response.data.source);

                setTypeFilterOptions(type_options);
                if (mode_options.length) setModeFilterOptions(mode_options);
                if (kra_status_options.length) setKraStatusFilterOptions(kra_status_options);
                if (status_options.length) setStatusFilterOptions(status_options);
                if (source_options.length) setSourceFilterOptions(source_options);

                setUiTypeFilter(type_options[0]); // default All
                if (mode_options.length) setUiModeFilter(mode_options[0]);
                if (kra_status_options.length) setUiKraStatusFilter(kra_status_options[0]);
                if (status_options.length) setUiStatusFilter(status_options[0]);
                if (source_options.length) setUiSourceFilter(source_options[0]);

                _getReasonList();
            }
        })
    }

    // API - get kyc reason
    const _getReasonList = () => {
        let url = 'onboarding/kyc/reason';
        APIService(false, url).then((response) => {
            if (response.status_code === 200) {
                const modified_data = response.data.map(item => {
                    return {
                        id: item.id,
                        name: item.label
                    }
                })
                setReasonList(modified_data);
            }
            _getKycSummary();
        })
    }

    // API - get kyc summary
    const _getKycSummary = () => {
        let url = 'onboarding/kyc/summary-new';
        APIService(false, url).then((response) => {
            if (response.status_code === 200) {
                setSummaryData(response.data);
            }
            // always release the page loader so a non-200 from the new endpoint can't hang the page
            setLoader(false);
        })
    }

    // API - search id from onboarding list
    const _getOnboardingList = () => {
        setApiLoader(true);
        let url = 'onboarding/kyc/list-new';
        let payload = JSON.stringify({
            query: searchQuery,
            kyc_onboarding_flow: uiTypeFilter?.value ?? null,
            // TODO: send the entity sub-type id when the Type filter supports non-individual flows (kyc_onboarding_flow not 1/2/10)
            kyc_flow_type: null,
            mode: uiModeFilter?.value ?? null,
            source: uiSourceFilter?.value ?? null,
            filter_id: uiStatusFilter?.value ?? null,
            kra_status: uiKraStatusFilter?.value ?? null,
            page_num: pageNumber,
            page_size: pageSize,
            start_date: startDate || null,
            end_date: endDate || null,
            start_time: selectedStartTime === null ? null : selectedStartTime,
            end_time: selectedEndTime === null ? null : selectedEndTime
        });
        APIService(true, url, payload).then((response) => {
            if (response.status_code === 200) {
                setOnboardingList(response.data);
                let total_pages = Math.ceil(response.data.number_of_records / response.data.page_size);
                setTotalPages(total_pages);
                setPaginationData({
                    rowsPerPageText: `${response.data.page} | Total records: ${response.data.number_of_records}`,
                });
                setTotalLength(response.data.number_of_records);
            }
            else {
                // toast.dismiss();
                // toast.error(response.message, {
                //     type: "error"
                // });
                setOnboardingList([]);
            }
            setApiLoader(false);
        })
    }

    // API - confirm onboarding download
    const _confirmOnboardingDownload = () => {
        setDownloadLoader(true);
        const customer_id = [...selectedCustomers]
        const url = "onboarding/kyc/confirm-download";

        const data = {
            "customer_ids": customer_id
        }

        APIService(true, url, data).then((response) => {
            if (response.status_code === 200) {
                setDownloadLoader(false);
                setUnlockModal(true);
            } else {
                toast.dismiss();
                toast.error(response.message, {
                    type: "error",
                });
            }
        })
    }

    const _downloadOnboarding = (pin) => {
        setApiLoader(true);
        const customer_id = [...selectedCustomers]
        const url = "onboarding/kyc/download";

        const data = {
            "customer_ids": customer_id,
            "otp": pin,
            "type": "DOWNLOAD"
        }

        APIService(true, url, data).then((response) => {
            if (response.status_code === 200) {
                window.open(response.data.url, "_blank");
            } else {
                toast.dismiss();
                toast.error(response.message, {
                    type: "error",
                });
            }
            setApiLoader(false);
            setUnlockModal(false);
            setSelectedCustomers(new Set());
        })
    }

    const _handleSelectAll = (event) => {
        event.stopPropagation();
        setSelectAll(!selectAll)
    }

    const _handleRowCheckBox = (row, event) => {
        // console.log("Row", row);
        event.stopPropagation();
        // console.log("before", selectedCustomers)
        if (selectedCustomers.has(row.customer_id)) {
            setSelectedCustomers((prevCustomerIds) => {
                const new_customer_ids = new Set(prevCustomerIds);
                new_customer_ids.delete(row.customer_id);
                return new_customer_ids;
            });
        }
        else {
            setSelectedCustomers((prevCustomerIds) => new Set([...prevCustomerIds, row.customer_id]));
        }
        setRowSelectFalse(true);
    }

    // for deselecting header checkbox in the table
    useEffect(() => {
        if (rowSelectFalse === true) {
            setSelectAll(false);
        }
    }, [rowSelectFalse])

    useEffect(() => {
        if (selectAll === true) {
            const customer_id_set = new Set(onboardingList.data.map((item) => item.customer_id));
            setSelectedCustomers((prevSet) => new Set([...prevSet, ...customer_id_set]));
            setRowSelectFalse(false);
        }
        else {
            if (rowSelectFalse === false) {
                setSelectedCustomers(new Set());
            }
        }
    }, [selectAll, onboardingList.data]);

    useEffect(() => {
        if (openFileViewer === true) {
            const modal = new Modal(document.getElementById("file-viewer"));
            modal.show();
        }
    }, [openFileViewer]);

    function _callbackFileViewerModalClose() {
        setFile("");
        setOpenFileViewer(false);
    }

    function _viewFile(url, event) {
        event?.stopPropagation();
        setFile({ url, type: _getFileViewerType(url) });
        setOpenFileViewer(true);
    }

    const _renderApproveRejectActions = (row, stopPropagation = false) => (
        <div className="d-flex align-items-center gap-8px"
            onClick={(event) => stopPropagation && event.stopPropagation()}
            data-disabled={`${actionLoader}`}>
            <div className="border-radius-8px border-all border-bright-gray bg-white p-2 d-flex align-items-center justify-content-center cursor-pointer"
                onMouseEnter={() => setOnHoverApprove(row.customer_id)}
                onMouseLeave={() => setOnHoverApprove(null)}
                onClick={(event) => {
                    event.stopPropagation();
                    _handleApproveModal(row);
                }}>
                <img draggable={false} src={require('../../Assets/Images/Onboarding/verified.svg').default} alt="Approve" width={20} height={20} />
            </div>
            {
                onHoverApprove === row.customer_id ?
                    <span className="position-absolute bg-light-sky-blue border-radius-4px p-1 e-font-12 e-poppins-regular top--30 left--30">
                        Approve KYC
                    </span>
                    :
                    null
            }
            <div className="border-radius-8px border-all border-bright-gray bg-white p-2 d-flex align-items-center justify-content-center cursor-pointer"
                onMouseEnter={() => setOnHoverReject(row.customer_id)}
                onMouseLeave={() => setOnHoverReject(null)}
                onClick={(event) => {
                    event.stopPropagation();
                    _handleRejectModal(row);
                }}>
                <img draggable={false} src={require('../../Assets/Images/Onboarding/failed.svg').default} alt="Reject" width={20} height={20} />
            </div>
            {
                onHoverReject === row.customer_id ?
                    <span className="position-absolute bg-light-sky-blue border-radius-4px p-1 e-font-12 e-poppins-regular top--30 left-45">
                        Reject KYC
                    </span>
                    :
                    null
            }
        </div>
    );

    // document row — reuses existing view (FileViewer) + download (window.open); disabled when no file
    const _renderDrawerDocumentRow = (icon, label, url) => {
        const hasFile = Boolean(url);
        return (
            <div className="e-drawer-doc-row d-flex align-items-center justify-content-between border-radius-8px padding-12px-all mb-1">
                <div className="d-flex align-items-center gap-8px">
                    <Icon icon={icon} size={20} />
                    <span className="color-black e-poppins-regular e-font-14 line-height-20px">{label}</span>
                </div>
                <div className="d-flex align-items-center gap-12px"
                    style={{ opacity: hasFile ? 1 : 0.3, pointerEvents: hasFile ? "auto" : "none" }}>
                    <Icon icon="document-view" size={20} className="e-doc-action-icon cursor-pointer"
                        onClick={(event) => _viewFile(url, event)} />
                    <Icon icon="document-download" size={20} className="e-doc-action-icon cursor-pointer"
                        onClick={() => window.open(url, "_blank")} />
                </div>
            </div>
        );
    };

    function _getShowingRange() {
        const total = totalLength || 0;
        if (total === 0) {
            return { start: 0, end: 0 };
        }
        const start = (pageNumber - 1) * pageSize + 1;
        const end = Math.min(pageNumber * pageSize, total);
        return { start, end };
    }

    function _getPaginationPageNumbers() {
        const pages = totalPages || 1;
        const maxVisible = 3;
        let start = Math.max(1, pageNumber - 1);
        let end = Math.min(pages, start + maxVisible - 1);
        start = Math.max(1, end - maxVisible + 1);
        return Array.from({ length: end - start + 1 }, (_, index) => start + index);
    }

    function _handlePageSelect(nextPage) {
        if (nextPage < 1 || nextPage > totalPages || nextPage === pageNumber) {
            return;
        }
        setPageNumber(nextPage);
        window.scrollTo({ top: 0, behavior: "smooth" });
    }

    // onboarding table data — columns aligned to onboarding verification design
    const ONBOARDING_COLUMNS = [
        {
            name: "Type",
            selector: row => row.type,
            sortable: false,
            left: true,
            minWidth: "110px",
            cell: row => (
                <span className="color-gray-500 e-poppins-regular e-font-14 line-height-20px">
                    {_formatTableCell(row.type)}
                </span>
            ),
        },
        {
            name: "UCC",
            selector: row => row.mt_client_id,
            sortable: true,
            left: true,
            minWidth: "110px",
            cell: row => (
                row.mt_client_id
                    ? <span className="e-ucc-link color-indigo-600 e-poppins-regular e-font-14 line-height-24px cursor-pointer"
                        onClick={(event) => _handleOpenDrawer(row, event)}>{row.mt_client_id}</span>
                    : <span className="color-outer-space">—</span>
            ),
        },
        {
            name: "PAN",
            selector: row => row.pan,
            sortable: false,
            left: true,
            minWidth: "130px",
            cell: row => (
                <span className="color-gray-500 e-poppins-regular e-font-14 line-height-20px">
                    {_formatTableCell(row.pan)}
                </span>
            ),
        },
        {
            name: "Name",
            selector: row => row.name,
            sortable: false,
            left: true,
            minWidth: "180px",
            cell: row => (
                <span className="color-gray-900 e-poppins-medium e-font-14 line-height-20px">
                    {_formatTableCell(row.name)}
                </span>
            ),
        },
        {
            name: "Created Date",
            selector: row => row.created,
            sortable: false,
            left: true,
            minWidth: "190px",
            cell: row => (
                <span className="color-gray-500 e-poppins-regular e-font-14 line-height-20px">
                    {_formatTableCell(row.created)}
                </span>
            ),
        },
        {
            name: "Esign Date",
            selector: row => row.esign_date,
            sortable: false,
            left: true,
            minWidth: "140px",
            cell: row => (
                <span className="color-gray-500 e-poppins-regular e-font-14 line-height-20px">
                    {_formatTableCell(row.esign_date)}
                </span>
            ),
        },
        {
            name: "Mode",
            selector: row => row.mode,
            sortable: false,
            left: true,
            minWidth: "110px",
            cell: row => (
                <span className="color-gray-500 e-poppins-regular e-font-14 line-height-20px">
                    {_formatTableCell(row.mode)}
                </span>
            ),
        },
        {
            name: "Source",
            selector: row => row.source,
            sortable: false,
            left: true,
            minWidth: "110px",
            cell: row => (
                <span className="color-gray-500 e-poppins-regular e-font-14 line-height-20px">
                    {_formatTableCell(row.source)}
                </span>
            ),
        },
        {
            name: "KRA",
            selector: row => row.kra,
            sortable: false,
            left: true,
            minWidth: "90px",
            cell: row => (
                <span className="color-gray-500 e-poppins-regular e-font-14 line-height-20px">
                    {_formatTableCell(row.kra)}
                </span>
            ),
        },
        {
            name: "KRA Status",
            selector: row => row.kra_status,
            sortable: false,
            left: true,
            minWidth: "130px",
            cell: row => (
                <span className="color-gray-500 e-poppins-regular e-font-14 line-height-20px">
                    {_formatTableCell(row.kra_status)}
                </span>
            ),
        },
        {
            name: "Status",
            selector: row => row.status,
            sortable: false,
            left: true,
            minWidth: "150px",
            cell: row => <OnboardingStatusChip label={row.status} />,
        },
    ];


    return (

        loader === true ?
            <CustomLoader />
            :
            <Fragment>
                <NavigationHeader
                    type={1}
                    title="Onboarding Verification"
                    subtitle="Manage and verify mutual fund investor onboarding requests." />
                <div className="ps-4 pe-lg-5 pe-md-2 pe-2 padding-32px-top">
                    {/* Summary Cards */}
                    <div className="row">
                        <div className="col-12">
                            <div className="d-flex gap-12px overflow-x-auto pb-2 scrollbar-container e-summary-cards-row">
                                {
                                    summaryData.map((card, key) => {
                                        const tooltipKey = card.label?.trim().toLowerCase();

                                        return (
                                            <div className="flex-shrink-0 e-summary-card-slot" style={{ minWidth: "110px", flex: "1 1 0" }} key={card.filter_id ?? key}>
                                                <UserSummary title={card.label}
                                                    type={4}
                                                    count={card.count}
                                                    borderBottomColor={_getSummaryBorderColor(card.label)}
                                                    countColor={_getSummaryCountColor(card.label)}
                                                    tooltip={SUMMARY_TOOLTIP_MAP[tooltipKey]}
                                                    isActive={uiStatusFilter?.value === card.filter_id}
                                                    onClick={apiLoader === true ? undefined : () => _handleSummaryCardClick(card)} />
                                            </div>
                                        );
                                    })
                                }
                            </div>
                        </div>
                    </div>

                    {/* Filter Bar */}
                    <div className="row mt-4">
                        <div className="col-12">
                            <div className="d-flex align-items-center gap-12px flex-wrap mb-3">
                                <div style={{ minWidth: "220px", flex: "1 1 300px" }}>
                                    <SearchInput placeholder="Search by Name, PAN, or UCC"
                                        key={`onboarding-search-${searchResetKey}`}
                                        defaultValue={searchQuery}
                                        value={searchQuery}
                                        valueChange={_handleSearchQuery}
                                        handleSearch={_getOnboardingList}
                                        divClass="w-100" />
                                </div>
                                <div style={{ minWidth: "120px", flex: "0 1 140px" }}>
                                    <CustomSearchSelectBox options={typeFilterOptions}
                                        value={uiTypeFilter}
                                        placeholder="Type"
                                        hide_error_text={true}
                                        onSelectChange={_handleTypeFilterUiChange} />
                                </div>
                                {/* Created Date / Esign date-type dropdown removed — date filtering is handled by the start/end date range (no `date` param in list-new)
                                <div style={{ minWidth: "140px", flex: "0 1 160px" }}>
                                    <CustomSearchSelectBox options={CREATED_DATE_FILTER_OPTIONS}
                                        value={uiCreatedDateFilter}
                                        placeholder="Created Date"
                                        hide_error_text={true}
                                        onSelectChange={_handleCreatedDateFilterUiChange} />
                                </div>
                                */}
                                <div style={{ minWidth: "120px", flex: "0 1 140px" }}>
                                    <CustomSearchSelectBox options={modeFilterOptions}
                                        value={uiModeFilter}
                                        placeholder="Mode"
                                        hide_error_text={true}
                                        onSelectChange={_handleModeFilterUiChange} />
                                </div>
                                <div style={{ minWidth: "130px", flex: "0 1 150px" }}>
                                    <CustomSearchSelectBox options={kraStatusFilterOptions}
                                        value={uiKraStatusFilter}
                                        placeholder="KRA Status"
                                        hide_error_text={true}
                                        onSelectChange={_handleKraStatusFilterUiChange} />
                                </div>
                                <div style={{ minWidth: "120px", flex: "0 1 140px" }}>
                                    <CustomSearchSelectBox options={statusFilterOptions}
                                        value={uiStatusFilter}
                                        placeholder="Status"
                                        hide_error_text={true}
                                        onSelectChange={_handleStatusFilterUiChange} />
                                </div>
                                <div style={{ minWidth: "120px", flex: "0 1 140px" }}>
                                    <CustomSearchSelectBox options={sourceFilterOptions}
                                        value={uiSourceFilter}
                                        placeholder="Source"
                                        hide_error_text={true}
                                        onSelectChange={_handleSourceFilterUiChange} />
                                </div>
                                <div className="d-flex align-items-center gap-12px ms-auto">
                                    <div className="position-relative" style={{ minWidth: "200px" }}>
                                        <CustomDateRangePicker
                                            startDate={startDate}
                                            leftOffset='right-0'
                                            endDate={endDate}
                                            placeholder='Start Date - End Date'
                                            popupRef={popupRef}
                                            showDateFilter={showDateFilter}
                                            handleShowDateFilter={_handleShowDateFilter}
                                            selectedRange={selectedRange}
                                            handleDateRangeChange={_handleDateRangeChange}
                                            clearDates={_handleClearDates} />
                                    </div>
                                    <CustomTimePicker
                                        label="Select Meeting Time"
                                        placeHolder='Start time'
                                        disabled={startDate === null ? true : false}
                                        selectedTime={selectedStartTime}
                                        handleSelectTime={_handleSelectStartTime}
                                        error={selectedStartTimeError}
                                        placement="bottomRight" />
                                    <CustomTimePicker
                                        label="Select Meeting Time"
                                        placeHolder="End time"
                                        disabled={endDate === null || selectedStartTime === null ? true : false}
                                        selectedTime={selectedEndTime}
                                        handleSelectTime={_handleSelectEndTime}
                                        error={selectedEndTimeError}
                                        disabledTime={_getDisabledEndTimes()}
                                        placement="bottomRight" />
                                </div>
                            </div>
                            {/* Row 2: filter actions */}
                            <div className="d-flex align-items-center gap-12px mb-3">
                                {
                                    permissionType !== 'read' && (
                                        <DownloadButton onPress={_confirmOnboardingDownload}
                                            loader={downloadLoader}
                                            label="Export to Excel"
                                            loadingLabel="Exporting..."
                                            icon="export-to-excel"
                                            size={24}
                                            className="flex-shrink-0 d-flex" />
                                    )
                                }
                                <button type="button"
                                    className="e-reset-filters-btn d-flex align-items-center gap-8px border-0 bg-transparent p-0 cursor-pointer"
                                    onClick={_handleResetFilters}>
                                    <Icon icon="reset-filters"
                                        size={16} />
                                    <span className="e-poppins-regular e-font-14 line-height-24px white-space-nowrap">
                                        Reset Filters
                                    </span>
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Table */}
                    <div className="row">
                        {
                            apiLoader === true ?
                                <div className="h-80vh">
                                    <CustomLoader />
                                </div>
                                :
                                onboardingList?.data?.length > 0 ?
                                    <div className="col-12" id="e-table-body">
                                        <DataTableContainer columns={ONBOARDING_COLUMNS}
                                            data={onboardingList.data}
                                            selectableRows={false}
                                            pagination={false}
                                            customStyles={TABLE_STYLE}
                                            rowClick={(row) => {
                                                _handleOpenDrawer(row)
                                            }} />
                                        <div className="d-flex justify-content-between align-items-center flex-wrap gap-3 mt-3 pt-3">
                                            <span className="color-outer-space e-poppins-regular e-font-14 line-height-24px mb-0">
                                                {
                                                    (() => {
                                                        const { start, end } = _getShowingRange();
                                                        return `Showing ${start}-${end} of ${totalLength || 0}`;
                                                    })()
                                                }
                                            </span>
                                            <div className="d-flex align-items-center gap-8px">
                                                <button type="button"
                                                    className={`border-all border-bright-gray border-radius-6px bg-white color-outer-space e-poppins-regular e-font-14 line-height-24px padding-8px-tb padding-16px-lr ${pageNumber === 1 ? "opacity-4 pointer-events-none" : "cursor-pointer"}`}
                                                    onClick={_previousPage}>
                                                    Previous
                                                </button>
                                                {
                                                    _getPaginationPageNumbers().map((page) => (
                                                        <button type="button"
                                                            key={page}
                                                            className={`border-all border-radius-6px e-poppins-regular e-font-14 line-height-24px padding-8px-tb padding-12px-lr cursor-pointer ${page === pageNumber ? "bg-ocean-boat-blue color-white border-primary-color" : "border-bright-gray bg-white color-outer-space"}`}
                                                            style={page === pageNumber ? { minWidth: "40px" } : { minWidth: "40px" }}
                                                            onClick={() => _handlePageSelect(page)}>
                                                            {page}
                                                        </button>
                                                    ))
                                                }
                                                <button type="button"
                                                    className={`border-all border-bright-gray border-radius-6px bg-white color-outer-space e-poppins-regular e-font-14 line-height-24px padding-8px-tb padding-16px-lr ${pageNumber >= totalPages ? "opacity-4 pointer-events-none" : "cursor-pointer"}`}
                                                    onClick={_nextPage}>
                                                    Next
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                    :
                                    <EmptyScreen title="Empty onboarding data!" />
                        }
                    </div>
                </div>

                {/* Onboarding detail drawer (reduced view from `details` endpoint) */}
                <div className="offcanvas offcanvas-end border-0 shadow-sm e-onboarding-drawer"
                    tabIndex="-1"
                    id="onboarding-detail-drawer">
                    <div className="offcanvas-header border-bottom border-bright-gray padding-26px-all d-flex align-items-center justify-content-between">
                        <h5 className="color-gray-900 e-poppins-semi-bold e-font-20 line-height-28px mb-0">
                            Verification Details
                        </h5>
                        <Icon icon="offcanvas-close"
                            size={20}
                            className="cursor-pointer color-old-silver"
                            onClick={_handleCloseDrawer} />
                    </div>
                    {
                        detailLoader === true ?
                            <div className="offcanvas-body d-flex align-items-center justify-content-center">
                                <CustomLoader />
                            </div>
                            :
                            detailData &&
                            <Fragment>
                                <div className="offcanvas-body padding-26px-all">
                                    {/* Header info — two columns */}
                                    <div className="row mb-3">
                                        <div className="col-6">
                                            <DrawerHeaderField label="UCC" value={detailData.mt_client_id ?? selectedRow?.mt_client_id} />
                                            <DrawerHeaderField label="Mobile" value={detailData.mobile ?? selectedRow?.mobile} />
                                            <DrawerHeaderField label="Mode" value={detailData.mode} />
                                            <DrawerHeaderField label="Created" value={detailData.created ?? selectedRow?.account_created_on} />
                                        </div>
                                        <div className="col-6">
                                            <DrawerHeaderField label="PAN" value={detailData.pan ?? selectedRow?.pan} />
                                            <DrawerHeaderField label="Email" value={detailData.email} />
                                            <DrawerHeaderField label="Source" value={detailData.source} />
                                            <DrawerHeaderField label="eSigned" value={detailData.esign_date} />
                                        </div>
                                    </div>

                                    {/* divider below top information */}
                                    <div className="border-bottom border-bright-gray mb-4"></div>

                                    {/* Section 1: Digilocker Information */}
                                    <div className="bg-light-sky-blue border-radius-8px padding-16px-all mb-4">
                                        <DrawerSectionTitle icon="digilocker-info" title="Digilocker Information" />
                                        <DrawerRowField label="Aadhaar Number" value={detailData.aadhaar} />
                                        <DrawerRowField label="Name" value={detailData.name} />
                                        {/* no `nationality` field yet — derive from tax_payer_india */}
                                        <DrawerRowField label="Nationality" value={detailData.tax_payer_india === true ? "Indian" : (detailData.tax_payer_india === false ? "Non-Resident" : null)} />
                                        <DrawerRowField label="Address" value={detailData.address} />
                                    </div>

                                    {/* Section 2: Additional Information */}
                                    <div className="mb-4">
                                        <p className="color-yankees-blue e-poppins-semi-bold e-font-16 line-height-24px mb-3">Additional Information</p>
                                        <div className="row">
                                            <div className="col-6"><DrawerStackField label="Father Name" value={detailData.father_name} /></div>
                                            <div className="col-6"><DrawerStackField label="Gender" value={detailData.gender} /></div>
                                            <div className="col-6"><DrawerStackField label="Marital Status" value={detailData.marital_status} /></div>
                                            <div className="col-6"><DrawerStackField label="Occupation" value={detailData.occupation} /></div>
                                            <div className="col-6"><DrawerStackField label="Annual Income" value={detailData.annual_income} /></div>
                                            <div className="col-6"><DrawerStackField label="Signature Status" value={detailData.signature_status} /></div>
                                        </div>
                                    </div>

                                    {/* Section 3: Declarations */}
                                    <div className="bg-amber-50 border-radius-8px padding-16px-all mb-4">
                                        <DrawerSectionTitle icon="declarations" title="Declarations" />
                                        <div className="d-flex align-items-center justify-content-between mb-2">
                                            <span className="color-yankees-blue e-poppins-regular e-font-14 line-height-20px">Tax Payer in India</span>
                                            {_renderDeclarationChip(_boolBadge(detailData.tax_payer_india ?? null, "Yes", "No"))}
                                        </div>
                                        <div className="d-flex align-items-center justify-content-between mb-2">
                                            <span className="color-yankees-blue e-poppins-regular e-font-14 line-height-20px">Politically Exposed</span>
                                            {_renderDeclarationChip(_boolBadge(detailData.politically_exposed ?? null, "Yes", "No"))}
                                        </div>
                                        <div className="d-flex align-items-center justify-content-between">
                                            <span className="color-yankees-blue e-poppins-regular e-font-14 line-height-20px">Terms &amp; Conditions</span>
                                            {_renderDeclarationChip(_boolBadge(detailData.terms_and_condition ?? null, "Accepted", "Not Accepted"))}
                                        </div>
                                    </div>

                                    {/* Section 4: Bank Details */}
                                    <div className="mb-4">
                                        <DrawerSectionTitle icon="bank-details" title="Bank Details" />
                                        <DrawerRowField label="Bank Name" value={detailData.bank_name} />
                                        <DrawerRowField label="Account Number" value={detailData.account_number} />
                                        <DrawerRowField label="IFSC Code" value={detailData.IFSC_code} />
                                        <DrawerRowField label="Account Type" value={detailData.bank_account_type} />
                                    </div>

                                    {/* Section 5: Nominee */}
                                    <div className="mb-4">
                                        <p className="color-yankees-blue e-poppins-semi-bold e-font-16 line-height-24px mb-3">Nominee</p>
                                        {
                                            // this endpoint has no explicit opt-in/opt-out flag — an empty nominee_name
                                            // is the only signal available that the customer opted out
                                            !detailData.nominee_name ?
                                                <p className="color-gray-500 e-poppins-regular e-font-14 line-height-20px mb-0">The customer has opted out of nominee</p>
                                                :
                                                <Fragment>
                                                    <DrawerRowField label="Name" value={detailData.nominee_name} />
                                                    <DrawerRowField label="Relationship" value={detailData.nominee_relation} />
                                                    <DrawerRowField label="Allocation" value={detailData.nominee_allocation} />
                                                </Fragment>
                                        }
                                    </div>

                                    {/* Section 6: Documents */}
                                    <div className="bg-light-grayish-white border-radius-8px padding-16px-all mb-4">
                                        <DrawerSectionTitle icon="Documents" title="Documents" />
                                        {_renderDrawerDocumentRow("declarations", "KYC Form", detailData.cvl_agreement)}
                                        {_renderDrawerDocumentRow("doc-signature", "Signature File", detailData.signature_file)}
                                        {_renderDrawerDocumentRow("document-selfie", "Selfie", detailData.selfie)}
                                        {_renderDrawerDocumentRow("xml-adhar-download", "XML Aadhaar Download", detailData.xml_aadhaar)}
                                    </div>

                                    {/* Section 7: Account Opening Form */}
                                    <div className="mb-4">
                                        <p className="color-yankees-blue e-poppins-semi-bold e-font-16 line-height-24px mb-3">Account Opening Form</p>
                                        <DrawerRowField label="Details Received On" value={detailData.kyc_submission_details?.detail_submitted_at_date ?? detailData.account_created_on ?? null} />
                                        <DrawerRowField label="Agreement Uploaded" value={detailData.kyc_submission_details?.agreement_uploaded_status ?? null} />
                                        {
                                            detailData.kyc_submission_details?.approval_status &&
                                            <div className="d-flex align-items-start justify-content-between gap-3 mb-2">
                                                <span className="color-yankees-blue e-poppins-regular e-font-14 line-height-20px white-space-nowrap">Approval status:</span>
                                                <span className={`e-poppins-medium e-font-14 line-height-20px text-end ${detailData.kyc_submission_details.approval_status !== 'Verified' ? 'color-yellow-orange' : 'color-sea-green'}`}>
                                                    {detailData.kyc_submission_details.approval_status}
                                                </span>
                                            </div>
                                        }
                                        {
                                            detailData.kyc_submission_details?.reason_summary &&
                                            <DrawerStackField label="Reason summary" value={detailData.kyc_submission_details.reason_summary} />
                                        }
                                    </div>

                                    {/* Section 8: Verification Status */}
                                    <div className="mb-2">
                                        <DrawerSectionTitle icon="verification-badge" title="Verification Status" />
                                        <div className="border-all border-bright-gray border-radius-8px padding-16px-all">
                                            {
                                                [
                                                    { label: "KRA Status", status: detailData.kyc_approved },
                                                    { label: "KYC Status", status: detailData.kra_verification },
                                                    { label: "BSE Submission", status: detailData.bse_submission },
                                                    { label: "Account Creation", status: detailData.account_creation },
                                                ].map((row, index, list) => {
                                                    const tone = DRAWER_PILL_STATUS_TONE[String(row.status ?? "").toLowerCase()] || "info";
                                                    return (
                                                        <div className={`d-flex align-items-center justify-content-between border-radius-8px padding-12px-all ${index === list.length - 1 ? "" : "mb-2"}`} key={row.label}
                                                            style={{ backgroundColor: DRAWER_PILL_TONES[tone].bg }}>
                                                            <span className="color-black e-poppins-regular e-font-14 line-height-20px">{row.label}</span>
                                                            <StatusPill status={row.status} />
                                                        </div>
                                                    );
                                                })
                                            }
                                        </div>
                                    </div>

                                    {/* Section 9: BSE Client Push — UCC / FATCA / AOF; hidden entirely when not applicable (imported client / no BSE client code) */}
                                    {
                                        detailData.ucc_status !== null && detailData.ucc_status !== undefined &&
                                        <div className="mb-2">
                                            <DrawerSectionTitle icon="verification-badge" title="BSE Client Push" />
                                            <div className="border-all border-bright-gray border-radius-8px padding-16px-all">
                                                {
                                                    detailData.ucc_status.fetched === false ?
                                                        <div className="d-flex align-items-center">
                                                            <Icon icon="info-circle" size={16} className="me-1" />
                                                            <p className="color-red mb-0 e-font-12">Status unavailable. Please retry.</p>
                                                        </div>
                                                        :
                                                        [
                                                            { label: "UCC", data: detailData.ucc_status.ucc },
                                                            { label: "FATCA", data: detailData.ucc_status.fatca },
                                                            { label: "AOF", data: detailData.ucc_status.aof },
                                                        ].map((row, index, list) => {
                                                            const statusLabel = _boolBadge(row.data?.status ?? null, "Success", "Failed");
                                                            const tone = DRAWER_PILL_STATUS_TONE[String(statusLabel ?? "").toLowerCase()] || "info";
                                                            return (
                                                                <div className={`d-flex align-items-center justify-content-between border-radius-8px padding-12px-all ${index === list.length - 1 ? "" : "mb-2"}`} key={row.label}
                                                                    style={{ backgroundColor: DRAWER_PILL_TONES[tone].bg }}>
                                                                    <span className="color-black e-poppins-regular e-font-14 line-height-20px">{row.label}</span>
                                                                    {
                                                                        statusLabel === "Failed" && row.data?.error ?
                                                                            <span className="e-tool-tip-container position-relative" data-tooltip-content={row.data.error}>
                                                                                <StatusPill status={statusLabel} />
                                                                            </span>
                                                                            :
                                                                            <StatusPill status={statusLabel} />
                                                                    }
                                                                </div>
                                                            );
                                                        })
                                                }
                                            </div>
                                        </div>
                                    }
                                </div>

                                {/* Sticky footer — shown only when the record is actionable and the admin can write */}
                                {
                                    detailData.can_approve === true  &&
                                    <div className="border-top border-bright-gray bg-white d-flex align-items-center gap-12px padding-16px-all">
                                        <button type="button"
                                            className="e-drawer-action-btn bg-green-600 flex-grow-1 border-0 color-white e-poppins-medium e-font-14 line-height-24px d-flex align-items-center justify-content-center gap-8px cursor-pointer"
                                            onClick={() => setConfirmAction('approve')}>
                                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M3.5 8.3 6.4 11.2 12.5 4.8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
                                            Approve
                                        </button>
                                        <button type="button"
                                            className="e-drawer-action-btn bg-red-600 flex-grow-1 border-0 color-white e-poppins-medium e-font-14 line-height-24px d-flex align-items-center justify-content-center gap-8px cursor-pointer"
                                            onClick={() => _handleRejectModal(detailData)}>
                                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M4.5 4.5 11.5 11.5M11.5 4.5 4.5 11.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /></svg>
                                            Reject
                                        </button>
                                        {/* dialog-box (reject-reason) button hidden for now — Reject already opens the KYC rejection reason modal
                                        <button type="button"
                                            className="e-drawer-dialog-btn bg-white border-all border-blue-gray d-flex align-items-center justify-content-center flex-shrink-0 cursor-pointer"
                                            onClick={() => _handleRejectModal(detailData)}>
                                            <Icon icon="dialog-box" size={20} />
                                        </button>
                                        */}
                                    </div>
                                }
                                {/* Repush footer — shown for rejected records so compliance can resubmit to BSE/KRA */}
                                {
                                    detailData.can_approve !== true &&
                                    (detailData.signup_progress_text === "ADMIN_REJECTED" || detailData.signup_progress_text === "KYC_REJECTED" || String(selectedRow?.status ?? "").trim().toLowerCase() === "rejected") &&
                                    <div className="border-top border-bright-gray bg-white d-flex align-items-center gap-12px padding-16px-all">
                                        <button type="button"
                                            className="e-drawer-action-btn bg-ocean-boat-blue flex-grow-1 border-0 color-white e-poppins-medium e-font-14 line-height-24px d-flex align-items-center justify-content-center gap-8px cursor-pointer"
                                            disabled={repushLoader}
                                            onClick={_repushKyc}>
                                            {repushLoader ? "Repushing..." : "Repush KYC"}
                                        </button>
                                    </div>
                                }
                            </Fragment>
                    }
                </div>
                {/* Approve / Reject confirmation modal */}
                {
                    confirmAction &&
                    <div className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                        style={{ backgroundColor: "rgba(15, 23, 42, 0.5)", zIndex: 1060 }}
                        onClick={() => { if (!confirmLoader) setConfirmAction(null); }}>
                        <div className="bg-white border-radius-16px"
                            style={{ width: "440px", maxWidth: "92vw", padding: "28px", boxShadow: "0px 20px 48px rgba(15, 23, 42, 0.2)" }}
                            onClick={(event) => event.stopPropagation()}>
                            <h5 className="color-black e-poppins-semi-bold e-font-20 line-height-28px mb-2">
                                {confirmAction === 'approve' ? "Approve Verification" : "Reject Verification"}
                            </h5>
                            <p className="color-outer-space e-poppins-regular e-font-16 line-height-24px mb-4">
                                {
                                    confirmAction === 'approve'
                                        ? `Are you sure you want to approve the verification for ${_dash(detailData?.name ?? selectedRow?.name)}? This will complete the onboarding process.`
                                        : "Are you sure you want to reject this verification? This action will notify compliance and mark the record as rejected."
                                }
                            </p>
                            <div className="d-flex justify-content-end gap-12px">
                                <button type="button"
                                    className="border-all border-bright-gray border-radius-8px bg-white color-charleston-green e-poppins-medium e-font-14 line-height-24px"
                                    style={{ padding: "10px 22px", opacity: confirmLoader ? 0.6 : 1 }}
                                    disabled={confirmLoader}
                                    onClick={() => setConfirmAction(null)}>
                                    Cancel
                                </button>
                                <button type="button"
                                    className="border-0 border-radius-8px color-white e-poppins-medium e-font-14 line-height-24px"
                                    style={{ padding: "10px 22px", backgroundColor: confirmAction === 'approve' ? Colors.green : Colors.red, opacity: confirmLoader ? 0.7 : 1 }}
                                    disabled={confirmLoader}
                                    onClick={_handleConfirmKycAction}>
                                    {confirmLoader ? "Please wait..." : "Confirm"}
                                </button>
                            </div>
                        </div>
                    </div>
                }
                {/* approve modal */}
                {
                    approveModal === true ?
                        <ApproveKYCModal
                            close={() => {
                                setActionLoader(false);
                                setApproveModal(false);
                                _handleCloseDrawer();
                                _getOnboardingList();
                            }} />
                        :
                        null
                }

                {/* reject modal */}
                {
                    rejectModal === true ?
                        <RejectKYCModal data={reasonList}
                            customerId={rejectCustomer.customer_id}
                            email={rejectCustomer.email}
                            mobile={rejectCustomer.mobile}
                            close={() => {
                                // Cancel / X / reject-submit close only the modal — keep the offcanvas open
                                setActionLoader(false);
                                setRejectModal(false);
                                _getOnboardingList();
                            }} />
                        :
                        null
                }

                {
                    unlockModal === true ?
                        <UnlockSIPModal
                            handleOTPSubmission={_downloadOnboarding}
                            close={() => {
                                setUnlockModal(false);
                            }} />
                        :
                        null
                }
                {
                    openFileViewer === true &&
                    <FileViewer data={file}
                        close={_callbackFileViewerModalClose} />
                }
            </Fragment >
    )
});
export default ManageOnboarding;